/**
 * Template-level Title + Description, in a drawer opened from the header's
 * "..." menu ("Edit details"). Mirrors `BriefSectionDrawer`: both fields
 * already patch the page's `name`/`description` state live, per keystroke, so
 * neither button discards a typed value. `Close` only dismisses. `Save` asks
 * the page to persist the template; the page keeps the drawer open (by not
 * calling back) when name or description is blank, so the inline error stays
 * visible.
 */
import { Field, FieldError, FieldLabel, Input, Textarea } from '@monksflow/monks-ui';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSheet } from '@/shared/ui/FormSheet';

export interface BriefTemplateDetailsDrawerProps {
  open: boolean;
  name: string;
  description: string;
  disabled: boolean;
  nameError?: string;
  descriptionError?: string;
  onNameChange: (next: string) => void;
  onDescriptionChange: (next: string) => void;
  /** Close/Escape/overlay dismissal. Never persists. */
  onClose: () => void;
  /** The Save button. Asks the page to persist. */
  onSave: () => void;
}

export function BriefTemplateDetailsDrawer(props: BriefTemplateDetailsDrawerProps) {
  const { t } = useTranslation('knowledge');

  return (
    <FormSheet
      open={props.open}
      onOpenChange={(next: boolean) => !next && props.onClose()}
      title={t('knowledgeHub.briefTemplateDetailsDrawer.templateDetails')}
      data-testid="brief-template-details-drawer"
      // 640 over the shell's 30rem default: the width this flow's sheets share.
      className="w-[640px]"
    >
      <FormSheet.Body className="flex flex-col gap-4">
        <Field data-invalid={props.nameError ? true : undefined}>
          <FieldLabel htmlFor="brief-template-details-drawer-name">
            {t('knowledgeHub.briefTemplateDetailsDrawer.title')}
            <span aria-hidden className="text-destructive">
              *
            </span>
          </FieldLabel>
          <Input
            id="brief-template-details-drawer-name"
            value={props.name}
            disabled={props.disabled}
            aria-invalid={props.nameError ? true : undefined}
            placeholder={t('knowledgeHub.briefTemplateDetailsDrawer.addATemplateName')}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              props.onNameChange(event.target.value)
            }
          />
          {props.nameError && (
            <FieldError data-testid="brief-template-name-error">{props.nameError}</FieldError>
          )}
        </Field>

        <Field data-invalid={props.descriptionError ? true : undefined}>
          <FieldLabel htmlFor="brief-template-details-drawer-description">
            {t('knowledgeHub.briefTemplateDetailsDrawer.description')}
            <span aria-hidden className="text-destructive">
              *
            </span>
          </FieldLabel>
          <Textarea
            id="brief-template-details-drawer-description"
            rows={3}
            value={props.description}
            disabled={props.disabled}
            aria-invalid={props.descriptionError ? true : undefined}
            placeholder={t('knowledgeHub.briefTemplateDetailsDrawer.addATemplateDescription')}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              props.onDescriptionChange(event.target.value)
            }
          />
          {props.descriptionError && (
            <FieldError data-testid="brief-template-description-error">
              {props.descriptionError}
            </FieldError>
          )}
        </Field>
      </FormSheet.Body>

      <FormSheet.Footer
        secondary={{
          label: t('knowledgeHub.briefTemplateDetailsDrawer.close'),
          'data-testid': 'brief-template-details-drawer-close',
          onClick: props.onClose,
        }}
        primary={{
          label: t('knowledgeHub.briefTemplateDetailsDrawer.save'),
          'data-testid': 'brief-template-details-drawer-save',
          onClick: props.onSave,
          disabled: props.disabled,
        }}
      />
    </FormSheet>
  );
}
